'use client'

import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import AlertTooltip from "@/components/alert-tooltip";
import { FormikProps } from 'formik';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { InfoIcon, RotateCcw } from 'lucide-react'
import { calculateUnitLoadTotals } from '@/lib/palletCalculations';

interface UnitTestingProps {
  formik: FormikProps<any>;
}

const palletPatterns = [
  "Block", "Split block", "Brick", "Pinwheel-1",
  "Pinwheel-2", "Pinwheel-3", "Row", "Split row",
  "Split row-1", "Pinwheel-4", "Pinwheel-5", "Split pinwheel"
];

const UnitTesting: React.FC<UnitTestingProps> = ({ formik, setHasUnsavedChanges }: { formik: any, setHasUnsavedChanges: any }) => {
  const [isUnitLoadWeightAutoCalculated, setIsUnitLoadWeightAutoCalculated] = useState(true);
  const [isUnitLoadHeightAutoCalculated, setIsUnitLoadHeightAutoCalculated] = useState(true);
  const [isUnitLoadLengthAutoCalculated, setIsUnitLoadLengthAutoCalculated] = useState(true);
  const [isUnitLoadWidthAutoCalculated, setIsUnitLoadWidthAutoCalculated] = useState(true);
  const [isCasesPerLayerAutoCalculated, setIsCasesPerLayerAutoCalculated] = useState(true);
  const yesNoOptions = ["Yes", "No"];
  console.log(formik.values)
  const parsePalletDimensions = (palletType: string) => {
    if (!palletType) return { length: 0, width: 0, height: 0 };
    console.log('Parsing pallet type:', palletType);
    const dimensions = palletType.split(' ')[0].split('x');
    console.log('Parsed dimensions:', dimensions);
    return {
      length: Number(dimensions[0]),
      width: Number(dimensions[1]),
      height: Number(dimensions[2])
    };
  };

  const convertToInches = (value: number, unit?: string) => {
    if (!unit || unit === 'inch') return value;
    if (unit === 'mm') return value / 25.4;
    return value;
  };

  const calculateOptimalPinwheel = (
    caseLength: number,
    caseWidth: number,
    palletLength: number,
    palletWidth: number
  ): { totalCases: number; unitLoadLength: number; unitLoadWidth: number } | null => {
    console.log('Pinwheel input parameters:', { caseLength, caseWidth, palletLength, palletWidth });

    // Return early if any dimension is invalid
    if (!caseLength || !caseWidth || !palletLength || !palletWidth ||
      isNaN(caseLength) || isNaN(caseWidth) || isNaN(palletLength) || isNaN(palletWidth)) {
      console.warn('Invalid dimensions for pinwheel calculation:', { caseLength, caseWidth, palletLength, palletWidth });
      return null;
    }

    let optimalSolution = null;
    let maxBoxes = 0;
    let bestAreaDifference = Infinity;
    const EPSILON = 1e-2; // Relaxed epsilon for floating point comparison

    // Maximum iteration for each parameter
    const MAX_ITERATIONS = 25; // Increased search space

    for (let m = 1; m < MAX_ITERATIONS; m++) {
      for (let n = 1; n < MAX_ITERATIONS; n++) {
        for (let p = 1; p < MAX_ITERATIONS; p++) {
          for (let q = 1; q < MAX_ITERATIONS; q++) {
            const unitWidth = m * caseWidth + p * caseLength;
            const unitLength = n * caseLength + q * caseWidth;
            const middleHoleArea = Math.abs(p * caseLength - m * caseWidth) * Math.abs(n * caseLength - q * caseWidth);
            const totalBoxes = 2 * (m * n + p * q);
            const totalBoxArea = totalBoxes * caseLength * caseWidth;
            const unitLoadArea = unitWidth * unitLength;

            // Calculate area difference
            const areaDifference = Math.abs((middleHoleArea + totalBoxArea) - unitLoadArea);

            // Check if this could be a valid pinwheel
            // Relaxed the pallet constraint to 120% allowance
            if (areaDifference < EPSILON &&
              unitWidth <= palletWidth * 1.2 && unitLength <= palletLength * 1.2) {

              // Either more boxes, or same boxes but better area match
              if (totalBoxes > maxBoxes ||
                (totalBoxes === maxBoxes && areaDifference < bestAreaDifference)) {
                maxBoxes = totalBoxes;
                bestAreaDifference = areaDifference;
                optimalSolution = {
                  totalCases: totalBoxes,
                  unitLoadLength: Number(unitLength.toFixed(2)),
                  unitLoadWidth: Number(unitWidth.toFixed(2))
                };
                console.log('Found potential pinwheel solution:', optimalSolution);
              }
            }
          }
        }
      }
    }

    console.log('Final pinwheel solution:', optimalSolution);
    return optimalSolution;
  };

  const calculateOptimalRow = (
    palletLength: number,
    palletWidth: number,
    caseLength: number,
    caseWidth: number
  ): { totalCases: number; unitLoadLength: number; unitLoadWidth: number } => {
    console.log(palletLength, palletWidth, caseLength, caseWidth, 'palletLength, palletWidth, caseLength, caseWidth')
    const calculateCases = (pLength: number, pWidth: number, cLength: number, cWidth: number) => {
      const leftoverLength = pLength % cLength;
      const leftoverWidth = pWidth % cWidth;
      let rotatedCasesWidthwise = 0;
      let rotatedCasesLengthwise = 0;
      console.log(leftoverLength, leftoverWidth, 'leftoverLength, leftoverWidth')
      let numCasesLengthwise = Math.floor(pLength / cLength);
      console.log(numCasesLengthwise, 'numCasesLengthwise')
      if (leftoverLength > 0.7 * cLength) {
        numCasesLengthwise = Math.floor((pLength + cLength) / cLength);
        console.log(numCasesLengthwise, 'numCasesLengthwise 2')
      } else if (leftoverLength > 0.7 * cWidth) {
        rotatedCasesLengthwise = Math.floor(leftoverLength / cWidth);
      }
      console.log(numCasesLengthwise, 'numCasesLengthwise')
      console.log(rotatedCasesLengthwise, 'rotatedCasesLengthwise')
      let numCasesWidthwise = Math.floor(pWidth / cWidth);
      console.log(numCasesWidthwise, 'numCasesWidthwise')
      if (leftoverWidth > 0.7 * cWidth) {
        numCasesWidthwise = Math.floor((pWidth + cWidth) / cWidth);
        console.log(numCasesWidthwise, 'numCasesWidthwise 2')
      } else if (leftoverWidth > 0.7 * cLength) {
        rotatedCasesWidthwise = Math.floor(leftoverWidth / cLength);
      }

      const unitLoadLength = numCasesLengthwise * cLength + rotatedCasesLengthwise * cWidth;
      const unitLoadWidth = numCasesWidthwise * cWidth + rotatedCasesWidthwise * cLength;

      const totalCases =
        Math.floor(numCasesLengthwise * numCasesWidthwise) +
        Math.floor(rotatedCasesLengthwise * (unitLoadWidth / cLength)) +
        Math.floor(rotatedCasesWidthwise * (unitLoadLength / cWidth));
      console.log(numCasesLengthwise, numCasesWidthwise, rotatedCasesLengthwise, rotatedCasesWidthwise, 'numCasesLengthwise, numCasesWidthwise, rotatedCasesLengthwise, rotatedCasesWidthwise')
      console.log(totalCases, unitLoadLength, unitLoadWidth, 'totalCases, unitLoadLength, unitLoadWidth')
      return { totalCases, unitLoadLength, unitLoadWidth };
    };

    // Try both orientations (length/width swapped)
    const defaultOrientation = calculateCases(palletLength, palletWidth, caseLength, caseWidth);
    const alternateOrientation = calculateCases(palletLength, palletWidth, caseWidth, caseLength);

    // Return the orientation that gives more cases
    return alternateOrientation.totalCases > defaultOrientation.totalCases
      ? {
        totalCases: alternateOrientation.totalCases,
        unitLoadLength: Number(alternateOrientation.unitLoadLength.toFixed(2)),
        unitLoadWidth: Number(alternateOrientation.unitLoadWidth.toFixed(2))
      }
      : {
        totalCases: defaultOrientation.totalCases,
        unitLoadLength: Number(defaultOrientation.unitLoadLength.toFixed(2)),
        unitLoadWidth: Number(defaultOrientation.unitLoadWidth.toFixed(2))
      };
  };

  const calculatePattern = () => {
    const selectedPattern = formik.values.unitLoadDetails.palletPatternDesign;
    const palletType = formik.values.palletDetails.palletTypes;

    console.log('Pattern inputs:', {
      selectedPattern,
      palletType,
      secondaryPkg: formik.values.secondaryPackagingDetails?.secondaryPkg,
      tertiaryPkg: formik.values.tertiaryPackagingDetails?.tertiaryPkg
    });

    if (!selectedPattern || !palletType) {
      console.log('Missing pattern or pallet type');
      return null;
    }

    const { length: palletLength, width: palletWidth } = parsePalletDimensions(palletType);
    console.log(palletLength, palletWidth, 'palletLengthhhh')
    let caseLength: number, caseWidth: number;

    // Check if secondary packaging is enabled
    if (formik.values.tertiaryPackagingDetails?.tertiaryPkg === "true") {
      // Convert tertiary case dimensions to inches if in mm
      const tertiaryLength = Number(formik.values.tertiaryPackagingDetails?.tertiaryCaseLength || 0);
      const tertiaryWidth = Number(formik.values.tertiaryPackagingDetails?.tertiaryCaseWidth || 0);
      const tertiaryLengthUnit = formik.values.tertiaryPackagingDetails?.tertiaryCaseLengthUnit;
      const tertiaryWidthUnit = formik.values.tertiaryPackagingDetails?.tertiaryCaseWidthUnit;

      caseLength = tertiaryLengthUnit === 'mm' ? tertiaryLength / 25.4 : tertiaryLength;
      caseWidth = tertiaryWidthUnit === 'mm' ? tertiaryWidth / 25.4 : tertiaryWidth;
      console.log('Using tertiary packaging dimensions (converted to inches):', { caseLength, caseWidth });

    } else {
      // Convert secondary case dimensions to inches if in mm
      const secondaryLength = Number(formik.values.secondaryPackagingDetails?.secondaryCaseLength || 0);
      const secondaryWidth = Number(formik.values.secondaryPackagingDetails?.secondaryCaseWidth || 0);
      const secondaryLengthUnit = formik.values.secondaryPackagingDetails?.secondaryCaseLengthUnit;
      const secondaryWidthUnit = formik.values.secondaryPackagingDetails?.secondaryCaseWidthUnit;

      caseLength = secondaryLengthUnit === 'mm' ? secondaryLength / 25.4 : secondaryLength;
      caseWidth = secondaryWidthUnit === 'mm' ? secondaryWidth / 25.4 : secondaryWidth;
      console.log('Using secondary packaging dimensions (converted to inches):', { caseLength, caseWidth });
    }

    console.log('Calculated dimensions:', {
      palletLength,
      palletWidth,
      caseLength,
      caseWidth
    });

    if (!caseLength || !caseWidth || !palletLength || !palletWidth) {
      console.log('Missing dimensions:', { caseLength, caseWidth, palletLength, palletWidth });
      return null;
    }

    let result;
    // Case insensitive check for "pinwheel" anywhere in the pattern name
    if (selectedPattern.toLowerCase().indexOf('pinwheel') !== -1) {
      console.log('Using pinwheel calculation');
      result = calculateOptimalPinwheel(caseLength, caseWidth, palletLength, palletWidth);
      console.log(result, 'result-pinwheel', caseLength, caseWidth, palletLength, palletWidth)
    } else {
      console.log('Using row calculation');
      result = calculateOptimalRow(palletLength, palletWidth, caseLength, caseWidth);
      console.log(result, 'result-row')
    }

    console.log('Calculation result:', result);
    return result;
  };

  const convertWeight = (value: number, fromUnit: string, toUnit: string): number => {
    // First convert to grams as base unit
    let valueInGrams = value;
    switch (fromUnit) {
      case 'lbs':
        valueInGrams = value * 453.592;
        break;
      case 'oz':
        valueInGrams = value * 28.3495;
        break;
      case 'kg':
        valueInGrams = value * 1000;
        break;
      case 'g':
        valueInGrams = value;
        break;
    }

    // Then convert from grams to target unit
    switch (toUnit) {
      case 'lbs':
        return valueInGrams / 453.592;
      case 'oz':
        return valueInGrams / 28.3495;
      case 'kg':
        return valueInGrams / 1000;
      case 'g':
        return valueInGrams;
      default:
        return value;
    }
  };

  const convertLength = (value: number, fromUnit: string, toUnit: string): number => {
    // Convert to inches first (1 mm = 0.0393701 inches)
    const valueInInches = fromUnit === 'mm' ? value * 0.0393701 : value;

    // Then convert to target unit
    return toUnit === 'mm' ? valueInInches / 0.0393701 : valueInInches;
  };

  const renderUnitSelect = (field: string, options: string[]) => (
    <Select
      defaultValue={options[0]}
      name={`unitLoadDetails.${field}Unit`}
      value={formik.values.unitLoadDetails[`${field}Unit`]}
      onValueChange={(value) => {
        const currentValue = formik.values.unitLoadDetails[field];
        const currentUnit = formik.values.unitLoadDetails[`${field}Unit`];

        // Skip conversion if no current value or unit
        if (!currentValue || !currentUnit) {
          formik.setFieldValue(`unitLoadDetails.${field}Unit`, value);
          return;
        }

        // Determine if this is a weight or length field
        const isWeightField = field.toLowerCase().includes('weight');
        const convertedValue = isWeightField
          ? convertWeight(Number(currentValue), currentUnit, value)
          : convertLength(Number(currentValue), currentUnit, value);

        formik.setFieldValue(`unitLoadDetails.${field}`, convertedValue.toFixed(2));
        formik.setFieldValue(`unitLoadDetails.${field}Unit`, value);
      }}
    >
      <SelectTrigger className=" w-[58px] lg:text-sm text-[8px] lg:w-[70px] h-4 px-1 lg:px-2 lg:h-6  bg-unitSelect rounded-[11px] lg:py-3 py-2 text-white">
        <SelectValue placeholder="Unit" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>{option}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const calculateTotalCasesPerUnitLoad = () => {
    const total = calculateUnitLoadTotals(formik.values).totalCasesPerUnitLoad;
    return total === undefined ? '' : total.toString();
  };

  const calculateItemsPerPallet = () => {
    const total = calculateUnitLoadTotals(formik.values).itemsPerPallet;
    return total === undefined ? '' : total.toString();
  };
  const calculateUnitLoadWeight = (): number => {
    const totalCasesPerUnitLoad = Number(calculateTotalCasesPerUnitLoad() || 0);
    console.log(totalCasesPerUnitLoad, 'totalCasesPerUnitLoadsss')
    const secondaryPkg = formik.values.secondaryPackagingDetails?.secondaryPkg;
    const tertiaryPkg = formik.values.tertiaryPackagingDetails?.tertiaryPkg;
    let total: number = 66; // Base pallet weight

    if (secondaryPkg === 'true') {
      const secondaryGrossWeight = Number(formik.values.secondaryPackagingDetails?.secondaryUnitGrossWeight || 0);
      const secondaryGrossWeightUnit = formik.values.secondaryPackagingDetails?.secondaryUnitGrossWeightUnit;
      console.log(secondaryGrossWeight, secondaryGrossWeightUnit, 'secondaryGrossWeightsss')
      // Convert to pounds based on unit
      let secondaryGrossWeightInPounds = secondaryGrossWeight;
      switch (secondaryGrossWeightUnit) {
        case 'oz':
          secondaryGrossWeightInPounds = secondaryGrossWeight / 16; // 16 oz = 1 lb
          break;
        case 'g':
          secondaryGrossWeightInPounds = secondaryGrossWeight / 453.592; // 453.592 g = 1 lb
          break;
        case 'kg':
          secondaryGrossWeightInPounds = secondaryGrossWeight * 2.20462; // 1 kg = 2.20462 lbs
          break;
      }

      total = (secondaryGrossWeightInPounds * totalCasesPerUnitLoad) + 66;
      console.log(secondaryGrossWeight, secondaryGrossWeightInPounds, totalCasesPerUnitLoad, 'secondaryGrossWeightsss')

    } else if (tertiaryPkg === 'true') {
      const tertiaryGrossWeight = Number(formik.values.tertiaryPackagingDetails?.tertiaryUnitGrossWeight || 0);
      const tertiaryGrossWeightUnit = formik.values.tertiaryPackagingDetails?.tertiaryUnitGrossWeightUnit;

      // Convert to pounds based on unit
      let tertiaryGrossWeightInPounds = tertiaryGrossWeight;
      switch (tertiaryGrossWeightUnit) {
        case 'oz':
          tertiaryGrossWeightInPounds = tertiaryGrossWeight / 16; // 16 oz = 1 lb
          break;
        case 'g':
          tertiaryGrossWeightInPounds = tertiaryGrossWeight / 453.592; // 453.592 g = 1 lb
          break;
        case 'kg':
          tertiaryGrossWeightInPounds = tertiaryGrossWeight * 2.20462; // 1 kg = 2.20462 lbs
          break;
      }

      total = (tertiaryGrossWeightInPounds * totalCasesPerUnitLoad) + 66;
    }
    return isNaN(total) ? 0 : total;
  };
  const calculateUnitLoadHeight = (): number => {
    const layersInPallet = Number(formik.values.unitLoadDetails.unitLayersPerUnitLoad || 0);
    const secondaryPkg = formik.values.secondaryPackagingDetails?.secondaryPkg;
    const tertiaryPkg = formik.values.tertiaryPackagingDetails?.tertiaryPkg;
    let total: number = 0;

    if (secondaryPkg === 'true') {
      const secondaryHeight = Number(formik.values.secondaryPackagingDetails?.secondaryCaseHeight || 0);
      const secondaryHeightUnit = formik.values.secondaryPackagingDetails?.secondaryCaseHeightUnit;
      // Convert to inches if in mm
      const secondaryHeightInches = secondaryHeightUnit === 'mm' ? secondaryHeight / 25.4 : secondaryHeight;
      total = layersInPallet * secondaryHeightInches + 5;
    }
    else if (tertiaryPkg === 'true') {
      const tertiaryHeight = Number(formik.values.tertiaryPackagingDetails?.tertiaryCaseHeight || 0);
      const tertiaryHeightUnit = formik.values.tertiaryPackagingDetails?.tertiaryCaseHeightUnit;
      // Convert to inches if in mm
      const tertiaryHeightInches = tertiaryHeightUnit === 'mm' ? tertiaryHeight / 25.4 : tertiaryHeight;
      total = layersInPallet * tertiaryHeightInches + 5;
    }
    return isNaN(total) ? 0 : total;
  };

  useEffect(() => {
    const result = calculatePattern();
    if (result) {
      if (formik.values.unitLoadDetails.isCasesPerLayerAutoCalculated) {
        formik.setFieldValue('unitLoadDetails.unitCasesPerLayer', result.totalCases);
      }
      if (formik.values.unitLoadDetails.isUnitLoadLengthAutoCalculated) {
        formik.setFieldValue('unitLoadDetails.unitLoadLength', result.unitLoadLength);
      }
      if (formik.values.unitLoadDetails.isUnitLoadWidthAutoCalculated) {
        formik.setFieldValue('unitLoadDetails.unitLoadWidth', result.unitLoadWidth);
      }
    }
  }, [
    formik.values.unitLoadDetails?.palletPatternDesign,
    formik.values.palletDetails?.palletTypes,
    formik.values.secondaryPackagingDetails?.secondaryPkg,
    formik.values.secondaryPackagingDetails?.secondaryCaseLength,
    formik.values.secondaryPackagingDetails?.secondaryCaseWidth,
    formik.values.secondaryPackagingDetails?.secondaryCaseLengthUnit,
    formik.values.secondaryPackagingDetails?.secondaryCaseWidthUnit,
    formik.values.tertiaryPackagingDetails?.tertiaryPkg,
    formik.values.tertiaryPackagingDetails?.tertiaryCaseLength,
    formik.values.tertiaryPackagingDetails?.tertiaryCaseWidth,
    formik.values.tertiaryPackagingDetails?.tertiaryCaseLengthUnit,
    formik.values.tertiaryPackagingDetails?.tertiaryCaseWidthUnit
  ]);

  useEffect(() => {
    if (formik.values.unitLoadDetails.isUnitLoadHeightAutoCalculated) {
      const calculatedHeight = calculateUnitLoadHeight();
      const heightUnit = formik.values.unitLoadDetails.unitLoadHeightUnit || 'inch';
      const convertedHeight = convertLength(calculatedHeight, 'inch', heightUnit);
      formik.setFieldValue('unitLoadDetails.unitLoadHeight', convertedHeight.toFixed(2));
    }
  }, [
    formik.values.unitLoadDetails?.unitLayersPerUnitLoad,
    formik.values.secondaryPackagingDetails?.secondaryPkg,
    formik.values.secondaryPackagingDetails?.secondaryCaseHeight,
    formik.values.secondaryPackagingDetails?.secondaryCaseHeightUnit,
    formik.values.tertiaryPackagingDetails?.tertiaryPkg,
    formik.values.tertiaryPackagingDetails?.tertiaryCaseHeight,
    formik.values.tertiaryPackagingDetails?.tertiaryCaseHeightUnit,
    formik.values.unitLoadDetails.isUnitLoadHeightAutoCalculated
  ]);

  useEffect(() => {
    if (formik.values.unitLoadDetails.isUnitLoadWeightAutoCalculated) {
      const calculatedWeight = calculateUnitLoadWeight();
      const currentUnit = formik.values.unitLoadDetails.unitLoadWeightUnit || 'lbs';
      const convertedWeight = convertWeight(calculatedWeight, 'lbs', currentUnit);
      formik.setFieldValue('unitLoadDetails.unitLoadWeight', convertedWeight.toFixed(2));
    }
  }, [
    formik.values.unitLoadDetails?.unitCasesPerLayer,
    formik.values.unitLoadDetails?.unitLayersPerUnitLoad,
    formik.values.secondaryPackagingDetails?.secondaryPkg,
    formik.values.secondaryPackagingDetails?.secondaryUnitGrossWeight,
    formik.values.secondaryPackagingDetails?.secondaryUnitGrossWeightUnit,
    formik.values.tertiaryPackagingDetails?.tertiaryPkg,
    formik.values.tertiaryPackagingDetails?.tertiaryUnitGrossWeight,
    formik.values.tertiaryPackagingDetails?.tertiaryUnitGrossWeightUnit,
    formik.values.unitLoadDetails.isUnitLoadWeightAutoCalculated
  ]);

  // Ensure all input values are properly initialized
  const getInputValue = (field: string) => {
    const value = formik.values.unitLoadDetails[field];
    return value === undefined || value === null ? '' : value.toString();
  };
  console.log()
  return (
    <div className="w-full pt-5">
      <div className="flex-center border-border-style-1 border w-full">
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">
            Pallet Pattern Design*
          </Label>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">
            Number of Layers*
          </Label>
        </div>
        <div className="label-bg flex items-center">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold  flex-grow">
            Unit Load length*
          </Label>
          {renderUnitSelect("unitLoadLength", ["inch", "mm"])}
        </div>
        <div className="label-bg flex items-center">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold  flex-grow">
            Unit Load Width*
          </Label>
          {renderUnitSelect("unitLoadWidth", ["inch", "mm"])}
        </div>
        <div className="label-bg flex items-center">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold  flex-grow flex items-center">
            Unit Load Height*
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Formula: Number of layers in the pallet * Package height</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <div className="flex items-center gap-1">
            {!formik.values.unitLoadDetails.isUnitLoadHeightAutoCalculated && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      title="Reset to auto-calculated value"
                      onClick={() => {
                        const calculatedValue = calculateUnitLoadHeight();
                        formik.setFieldValue('unitLoadDetails.unitLoadHeight', calculatedValue.toFixed(2));
                        formik.setFieldValue('unitLoadDetails.isUnitLoadHeightAutoCalculated', true);
                        setHasUnsavedChanges(true);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <RotateCcw className="h-3 w-3 text-gray-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset to auto-calculated value</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {renderUnitSelect("unitLoadHeight", ["inch", "mm"])}
          </div>
        </div>
      </div>

      <div className="flex-center border-border-style-1 border w-full">
        <div className="relative w-full">
          <Select
            name="unitLoadDetails.palletPatternDesign"
            value={formik.values.unitLoadDetails.palletPatternDesign}
            onValueChange={(value) => formik.setFieldValue("unitLoadDetails.palletPatternDesign", value)}
          >
            <SelectTrigger className="w-full bg-selectBlue border-2 border-required">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {palletPatterns.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.unitLayersPerUnitLoad"
            placeholder="Enter"
            type="number"
            value={formik.values.unitLoadDetails.unitLayersPerUnitLoad}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={cn(
              'border-2 border-required',

            )}
          />
          {/* {formik.touched.unitLoadDetails?.unitLayersPerUnitLoad &&
            formik.errors.unitLoadDetails?.unitLayersPerUnitLoad && (
              <AlertTooltip
                message={formik.errors.unitLoadDetails.unitLayersPerUnitLoad as string}
              />
            )} */}
        </div>
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.unitLoadLength"
            placeholder="Enter"
            type="number"
            value={getInputValue('unitLoadLength')}
            onChange={(e) => {
              formik.handleChange(e);
              formik.setFieldValue('unitLoadDetails.isUnitLoadLengthAutoCalculated', false);
            }}
            onBlur={formik.handleBlur}
            className={cn(
              'border-2 border-required',
              formik.values.unitLoadDetails.isUnitLoadLengthAutoCalculated && "bg-bgYellow"
            )}
          />
          {!formik.values.unitLoadDetails.isUnitLoadLengthAutoCalculated && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    title="Reset to auto-calculated value"
                    onClick={() => {
                      const result = calculatePattern();
                      if (result) {
                        formik.setFieldValue('unitLoadDetails.unitLoadLength', result.unitLoadLength);
                      }
                      formik.setFieldValue('unitLoadDetails.isUnitLoadLengthAutoCalculated', true);
                      setHasUnsavedChanges(true);
                    }}
                    className="absolute right-16 top-2 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <RotateCcw className="h-3 w-3 text-gray-500" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset to auto-calculated value</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {formik.touched.unitLoadDetails?.unitLoadLength &&
            formik.errors.unitLoadDetails?.unitLoadLength && (
              <AlertTooltip
                message={formik.errors.unitLoadDetails.unitLoadLength as string}
              />
            )}
        </div>
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.unitLoadWidth"
            placeholder="Enter"
            type="number"
            value={getInputValue('unitLoadWidth')}
            onChange={(e) => {
              formik.handleChange(e);
              formik.setFieldValue('unitLoadDetails.isUnitLoadWidthAutoCalculated', false);
            }}
            onBlur={formik.handleBlur}
            className={cn(
              'border-2 border-required',
              formik.values.unitLoadDetails.isUnitLoadWidthAutoCalculated && "bg-bgYellow"
            )}
          />
          {!formik.values.unitLoadDetails.isUnitLoadWidthAutoCalculated && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    title="Reset to auto-calculated value"
                    onClick={() => {
                      const result = calculatePattern();
                      if (result) {
                        formik.setFieldValue('unitLoadDetails.unitLoadWidth', result.unitLoadWidth);
                      }
                      formik.setFieldValue('unitLoadDetails.isUnitLoadWidthAutoCalculated', true);
                      setHasUnsavedChanges(true);
                    }}
                    className="absolute right-16 top-2 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <RotateCcw className="h-3 w-3 text-gray-500" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset to auto-calculated value</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {formik.touched.unitLoadDetails?.unitLoadWidth &&
            formik.errors.unitLoadDetails?.unitLoadWidth && (
              <AlertTooltip
                message={formik.errors.unitLoadDetails.unitLoadWidth as string}
              />
            )}
        </div>
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.unitLoadHeight"
            placeholder="Enter"
            type="number"
            value={getInputValue('unitLoadHeight')}
            onChange={(e) => {
              formik.handleChange(e);
              formik.setFieldValue('unitLoadDetails.isUnitLoadHeightAutoCalculated', false);
            }}
            onBlur={formik.handleBlur}
            className={cn(
              'border-2 border-required',
              formik.values.unitLoadDetails.isUnitLoadHeightAutoCalculated && "bg-bgYellow"
            )}
          />
          {formik.touched.unitLoadDetails?.unitLoadHeight &&
            formik.errors.unitLoadDetails?.unitLoadHeight && (
              <AlertTooltip
                message={formik.errors.unitLoadDetails.unitLoadHeight as string}
              />
            )}
        </div>
      </div>

      <div className="flex-center border-border-style-1 border w-full">
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">
            Unit Load Weight*
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="h-4 w-4 text-muted-foreground cursor-help ml-1" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Formula: (Package gross weight * Total cases per unitload) + 66 lbs (pallet weight)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <div className="flex items-center gap-1">
            {!formik.values.unitLoadDetails.isUnitLoadWeightAutoCalculated && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      title="Reset to auto-calculated value"
                      onClick={() => {
                        const calculatedValue = calculateUnitLoadWeight();
                        formik.setFieldValue('unitLoadDetails.unitLoadWeight', calculatedValue.toFixed(2));
                        formik.setFieldValue('unitLoadDetails.isUnitLoadWeightAutoCalculated', true);
                        setHasUnsavedChanges(true);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <RotateCcw className="h-3 w-3 text-gray-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reset to auto-calculated value</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {renderUnitSelect("unitLoadWeight", ["lbs", "oz", 'g', 'kg'])}
          </div>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">
            Cases Per Layer*
          </Label>
        </div>
      </div>

      <div className="flex-center border-border-style-1 border w-full">
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.unitLoadWeight"
            placeholder="Enter"
            type="number"
            value={getInputValue('unitLoadWeight')}
            onChange={(e) => {
              formik.handleChange(e);
              formik.setFieldValue('unitLoadDetails.isUnitLoadWeightAutoCalculated', false);
              // setHasUnsavedChanges(true);
            }}
            onBlur={formik.handleBlur}
            className={cn(
              'border-2 border-required',
              formik.values.unitLoadDetails.isUnitLoadWeightAutoCalculated && "bg-bgYellow"
            )}
          />
          {formik.touched.unitLoadDetails?.unitLoadWeight &&
            formik.errors.unitLoadDetails?.unitLoadWeight && (
              <AlertTooltip
                message={formik.errors.unitLoadDetails.unitLoadWeight as string}
              />
            )}
        </div>
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.unitCasesPerLayer"
            placeholder="Enter"
            type="number"
            value={getInputValue('unitCasesPerLayer')}
            onChange={(e) => {
              formik.handleChange(e);
              formik.setFieldValue('unitLoadDetails.isCasesPerLayerAutoCalculated', false);
            }}
            onBlur={formik.handleBlur}
            className={cn(
              'border-2 border-required',
              formik.values.unitLoadDetails.isCasesPerLayerAutoCalculated && "bg-bgYellow"
            )}
          />
          {!formik.values.unitLoadDetails.isCasesPerLayerAutoCalculated && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    title="Reset to auto-calculated value"
                    onClick={() => {
                      const result = calculatePattern();
                      if (result) {
                        formik.setFieldValue('unitLoadDetails.unitCasesPerLayer', result.totalCases);
                      }
                      formik.setFieldValue('unitLoadDetails.isCasesPerLayerAutoCalculated', true);
                      setHasUnsavedChanges(true);
                    }}
                    className="absolute right-2 top-2 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <RotateCcw className="h-3 w-3 text-gray-500" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset to auto-calculated value</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      <div className="flex-center border-border-style-1 border w-full">
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">
            Machine Assembled
          </Label>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">Plant</Label>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">Plant Line</Label>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">New Plant Line</Label>
        </div>
      </div>

      <div className="flex-center border-border-style-1 border w-full">
        <div className="relative w-full">
          <Select
            name="unitLoadDetails.machineAssembled"
            value={formik.values.unitLoadDetails.machineAssembled}
            onValueChange={(value) => formik.setFieldValue("unitLoadDetails.machineAssembled", value)}
          >
            <SelectTrigger className="w-full bg-selectBlue">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {yesNoOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.plant"
            placeholder="Enter"
            value={formik.values.unitLoadDetails.plant}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className="relative w-full">
          <Input
            name="unitLoadDetails.plantLine"
            placeholder="Enter"
            value={formik.values.unitLoadDetails.plantLine}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className="relative w-full">
          <Select
            name="unitLoadDetails.newPlantLine"
            value={formik.values.unitLoadDetails.newPlantLine}
            onValueChange={(value) => formik.setFieldValue("unitLoadDetails.newPlantLine", value)}
          >
            <SelectTrigger className="w-full bg-selectBlue">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {yesNoOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-center border-border-style-1 border w-full">
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">EngHelp</Label>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">Variety Pack</Label>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">
            Total Cases Per Unit Load
          </Label>
        </div>
        <div className="label-bg">
          <Label className="label lg:text-sm text-[px] font-semibold lg:font-bold ">Items/Pallet</Label>
        </div>
      </div>

      <div className="flex-center border-border-style-1 border w-full">
        <div className="relative w-full">
          <Select
            name="unitLoadDetails.engHelp"
            value={formik.values.unitLoadDetails.engHelp}
            onValueChange={(value) => formik.setFieldValue("unitLoadDetails.engHelp", value)}
          >
            <SelectTrigger className="w-full bg-selectBlue">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {yesNoOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative w-full">
          <Select
            name="unitLoadDetails.varietyPack"
            value={formik.values.unitLoadDetails.varietyPack}
            onValueChange={(value) => formik.setFieldValue("unitLoadDetails.varietyPack", value)}
          >
            <SelectTrigger className="w-full bg-selectBlue">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {yesNoOptions.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="relative w-full">
          <Input
            value={calculateTotalCasesPerUnitLoad()}
            readOnly={true}
            className="bg-gray-100"
          />
        </div>
        <div className="relative w-full">
          <Input
            value={calculateItemsPerPallet()}
            readOnly={true}
            className="bg-gray-100"
          />
        </div>
      </div>

      <div className="w-full mt-5">
        <div className="label-bg border border-border-style-1">
          <Label className="label lg:text-sm text-[px] mx-auto font-bold text-blue text-center">
            Reference
          </Label>
        </div>
        <div className="w-full h-[400px]">
          <img
            src={"/reference.jpg"}
            alt="reference"
          />
        </div>
      </div>
    </div>
  );
};

export default UnitTesting;
